/**
 * placement.js - where this child starts, and what plays next.
 *
 * PURE, and now very thin: it declares this game's coverage to the engine and
 * gets back an answer that already accounts for it.
 *
 * ---------------------------------------------------------------------------
 * WHAT USED TO BE HERE
 *
 * This file previously re-clamped every answer the engine gave, because the
 * engine modelled phase coverage (section 8.7) and not level coverage, and
 * this game implements 9 of the 17 levels in phases 1-3. That was GAME-01.md
 * section 7.1, and it is now fixed in the engine: `levelsAvailable` sits
 * beside `phasesAvailable` and `placeInGame`, `getNextLevel` and
 * `getPreviousLevel` all honour it.
 *
 * The clamping rule itself - downward, never upward, because section 2.4 says
 * a child placed above their level fails repeatedly - moved with it, into
 * framework.nearestAvailableLevel(). One rule, in the place every game reads.
 */

import { getNextLevel, getPreviousLevel } from '../../../../engine/framework.js';
import { GAME } from '../content/index.js';

/** This game's coverage, in the shape every engine navigation call takes. */
export const COVERAGE_OPTS = Object.freeze({
  availablePhases: GAME.phasesAvailable,
  availableLevels: GAME.levelsAvailable,
});

/**
 * WHERE PLACEMENT WENT
 *
 * `placeChild()` was here, and it did what every game has to do: read the
 * CHILD's phase and level, hand the engine this game's coverage, and use the
 * answer unmodified. None of that is about bubbles, and one of the two games
 * had the field name wrong, so it is now
 * `components/game/shared/profile.js placeChildInGame(child, GAME)`.
 */

/** The next level this game implements, or null at the end of the content. */
export function nextInGame(phase, level) {
  return getNextLevel(phase, level, COVERAGE_OPTS);
}

/** The previous one, for a step back (section 8.4). */
export function previousInGame(phase, level) {
  return getPreviousLevel(phase, level, COVERAGE_OPTS);
}

/** Position within this game's own content, for the progress display. */
export function contentProgress(phase, level) {
  const levels = GAME.levelsAvailable;
  const index = levels.findIndex((l) => l.phase === phase && l.level === level);
  return {
    index: index === -1 ? 0 : index,
    total: levels.length,
    isLast: index === levels.length - 1,
  };
}

export default { nextInGame, previousInGame, contentProgress, COVERAGE_OPTS };
